/**
 * App.jsx — FlowGuard live dashboard (replaces synthetic FlowGuardDashboard.jsx)
 * Drop this file into flowguard-ui/src/App.jsx
 *
 * CHANGES FROM SYNTHETIC VERSION:
 *  - All data fetched from Flask endpoints (localhost:5050)
 *  - useEffect hooks for each data domain
 *  - Loading / error states per panel
 *  - Model metrics, predictions, pipe risk, SSO events all live
 *  - Forecast panel ready (calls /api/forecast when endpoint exists)
 *  - Branding: FlowGuard (product) / ClearFlow Analytics (company)
 *
 * Flask endpoints consumed:
 *  GET /api/status
 *  GET /api/model-summary
 *  GET /api/predictions
 *  GET /api/pipe-risk
 *  GET /api/map/stats
 *  GET /api/sso-events
 *  GET /api/forecast        ← not yet built; panel shows "coming soon" gracefully
 */

import PipeNetworkMap from './PipeNetworkMap';
import { useState, useEffect, useCallback, Component } from "react";
import {
  Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";

// ── Error boundary — contains tab-level crashes so the whole app stays up ─────
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: 20, background: "#1c0a0a", border: "1px solid #7f1d1d",
          borderRadius: 8, fontSize: 12, color: "#fca5a5",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>⚠ Panel error: {this.state.error?.message}</span>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              background: "#7f1d1d", border: "none", color: "#fca5a5",
              borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontSize: 11,
            }}
          >Reset</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Config ────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5050";

// ── Fetch helper ─────────────────────────────────────────────────
async function apiFetch(path) {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

// ── Color helpers ─────────────────────────────────────────────────
const TIER_COLORS = {
  Critical: "#dc2626",
  High:     "#f97316",
  Medium:   "#eab308",
  Low:      "#22c55e",
};

const tierColor  = (t) => TIER_COLORS[t] || "#6b7280";
const probColor  = (p) => p >= 0.60 ? "#dc2626" : p >= 0.40 ? "#f97316" : p >= 0.20 ? "#eab308" : "#4ade80";
const rainColor  = (r) => r >= 1.0 ? "#38bdf8" : r >= 0.5 ? "#7dd3fc" : r >= 0.25 ? "#bae6fd" : "#1e3a5f";

// ── Sub-components ────────────────────────────────────────────────

const Spinner = ({ size = 20 }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid #1e293b`,
    borderTop: `2px solid #38bdf8`,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  }} />
);

const LoadingPanel = ({ label }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: 12, padding: 40, color: "#475569", fontSize: 12,
  }}>
    <Spinner /> Fetching {label}…
  </div>
);

const ErrorPanel = ({ msg, onRetry }) => (
  <div style={{
    padding: 20, background: "#1c0a0a", border: "1px solid #7f1d1d",
    borderRadius: 6, fontSize: 12, color: "#fca5a5",
    display: "flex", justifyContent: "space-between", alignItems: "center",
  }}>
    <span>⚠ {msg}</span>
    {onRetry && (
      <button onClick={onRetry} style={{
        background: "#7f1d1d", border: "none", color: "#fca5a5",
        borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontSize: 11,
      }}>Retry</button>
    )}
  </div>
);

const StatCard = ({ label, value, sub, accent, loading }) => (
  <div style={{
    background: "#0f172a",
    border: `1px solid ${accent ? accent + "44" : "#1e3a5f"}`,
    borderRadius: 8, padding: "16px 20px", minWidth: 140,
    position: "relative", overflow: "hidden",
  }}>
    {accent && (
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, ${accent}88, transparent)`,
      }} />
    )}
    <div style={{
      fontSize: 10, color: "#64748b",
      letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6,
    }}>{label}</div>
    {loading ? (
      <div style={{ height: 36, display: "flex", alignItems: "center" }}>
        <Spinner size={16} />
      </div>
    ) : (
      <div style={{
        fontSize: 26, fontWeight: 700,
        color: accent || "#f8fafc",
        fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
        letterSpacing: "-0.02em",
      }}>{value ?? "—"}</div>
    )}
    {sub && <div style={{ fontSize: 10, color: "#475569", marginTop: 4 }}>{sub}</div>}
  </div>
);

const SectionHeader = ({ title, sub, badge }) => (
  <div style={{ marginBottom: 16, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: "#94a3b8",
        letterSpacing: "0.08em", textTransform: "uppercase",
      }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: "#475569", marginTop: 3 }}>{sub}</div>}
    </div>
    {badge && (
      <span style={{
        background: "#0f2d1a", border: "1px solid #166534",
        color: "#4ade80", fontSize: 10, padding: "2px 8px", borderRadius: 10,
      }}>{badge}</span>
    )}
  </div>
);

const Panel = ({ children, style = {} }) => (
  <div style={{
    background: "#0f172a",
    border: "1px solid #1e293b",
    borderRadius: 8, padding: 20,
    ...style,
  }}>{children}</div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{
      background: "#1e293b", border: "1px solid #334155",
      borderRadius: 6, padding: "10px 14px", fontSize: 12, maxWidth: 220,
    }}>
      <div style={{ color: "#94a3b8", marginBottom: 6, fontFamily: "monospace" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || "#f8fafc", marginBottom: 2 }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toFixed(3) : p.value}</strong>
        </div>
      ))}
      {d?.overflow_occurred === 1 && (
        <div style={{
          color: "#f97316", marginTop: 6,
          paddingTop: 6, borderTop: "1px solid #334155",
          fontStyle: "italic", fontSize: 11,
        }}>⚠ Overflow recorded</div>
      )}
    </div>
  );
};

// ── Custom hook: fetch with loading/error state ───────────────────
function useApiData(path, deps = []) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch(path);
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, refetch: load };
}

// ═══════════════════════════════════════════════════════════════════
// TABS
// ═══════════════════════════════════════════════════════════════════

// ── Overview Tab ──────────────────────────────────────────────────
function OverviewTab({ mapStats, ssoEvents }) {
  const msLoading = mapStats.loading;
  const ssLoading = ssoEvents.loading;

  // Derive summary numbers from live SSO data
  const summary = ssoEvents.data?.summary || [];
  const totalEvents  = summary.reduce((a, r) => a + r.count,  0);
  const totalVolMG   = summary.reduce((a, r) => a + r.volume_mg, 0);
  const wetCount     = summary.filter(r => r.cause_category === "wet_weather")
                               .reduce((a, r) => a + r.count, 0);
  const wetPct       = totalEvents > 0 ? Math.round((wetCount / totalEvents) * 100) : 0;

  // Build cause × quarter bar data from live summary
  const causeMap = {};
  summary.forEach(row => {
    if (!row.quarter) return;
    const cause = row.cause_category.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    if (!causeMap[cause]) causeMap[cause] = { cause, q1: 0, q2: 0, q3: 0, q4: 0 };
    const qKey = `q${row.quarter}`;
    if (causeMap[cause][qKey] !== undefined) causeMap[cause][qKey] += row.count;
  });
  const causeData = Object.values(causeMap).sort((a, b) =>
    (b.q1+b.q2+b.q3+b.q4) - (a.q1+a.q2+a.q3+a.q4)
  ).slice(0, 6);

  const tierCounts = mapStats.data?.by_tier || {};
  const totalPipes = mapStats.data?.total_pipes || 0;

  const tierDist = ["Critical","High","Medium","Low"].map(t => ({
    tier: t, count: tierCounts[t] || 0,
  }));

  return (
    <div>
      {/* KPI row */}
      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        <StatCard label="SSO Events" value={ssLoading ? null : totalEvents || "—"}
          sub="All causes, all years loaded" accent="#38bdf8" loading={ssLoading} />
        <StatCard label="Total Volume" value={ssLoading ? null : totalVolMG > 0 ? `${totalVolMG.toFixed(0)} MG` : "—"}
          sub="Across all loaded years" accent="#f97316" loading={ssLoading} />
        <StatCard label="Wet-Weather Share" value={ssLoading ? null : wetPct > 0 ? `${wetPct}%` : "—"}
          sub={`${wetCount} of ${totalEvents} events`} accent="#a78bfa" loading={ssLoading} />
        <StatCard label="Critical Pipes" value={msLoading ? null : tierCounts.Critical ?? "—"}
          sub="Score > 80 — VCP/CIP unrehabbed" accent="#dc2626" loading={msLoading} />
        <StatCard label="Pipes Scored" value={msLoading ? null : totalPipes.toLocaleString() || "—"}
          sub="Active segments in GIS" accent="#64748b" loading={msLoading} />
      </div>

      {ssoEvents.error && <ErrorPanel msg={`SSO events: ${ssoEvents.error}`} onRetry={ssoEvents.refetch} />}
      {mapStats.error  && <ErrorPanel msg={`Map stats: ${mapStats.error}`}   onRetry={mapStats.refetch} />}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Cause × Quarter chart */}
        <Panel>
          <SectionHeader title="SSO Events by Cause & Quarter" sub="From live sso_events data" />
          {ssLoading ? <LoadingPanel label="SSO events" /> : causeData.length === 0 ? (
            <div style={{ color: "#475569", fontSize: 12, padding: 20 }}>No data loaded.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={causeData} barSize={8}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="cause" tick={{ fill: "#64748b", fontSize: 9 }}
                  tickFormatter={v => v.length > 10 ? v.slice(0,10)+"…" : v} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="q1" name="Q1" stackId="a" fill="#38bdf8" />
                <Bar dataKey="q2" name="Q2" stackId="a" fill="#2563eb" />
                <Bar dataKey="q3" name="Q3" stackId="a" fill="#7c3aed" />
                <Bar dataKey="q4" name="Q4" stackId="a" fill="#475569" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        {/* Pipe tier distribution */}
        <Panel>
          <SectionHeader title="Pipe Risk Distribution"
            sub={msLoading ? "Loading…" : `${totalPipes.toLocaleString()} active segments scored`} />
          {msLoading ? <LoadingPanel label="map stats" /> : (
            <>
              {tierDist.map(({ tier, count }) => (
                <div key={tier} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ color: tierColor(tier), fontSize: 12 }}>{tier}</span>
                    <span style={{ color: "#94a3b8", fontFamily: "monospace", fontSize: 11 }}>
                      {count.toLocaleString()}
                      {totalPipes > 0 && ` (${(count/totalPipes*100).toFixed(0)}%)`}
                    </span>
                  </div>
                  <div style={{ background: "#1e293b", borderRadius: 4, height: 6 }}>
                    <div style={{
                      background: tierColor(tier), height: 6, borderRadius: 4,
                      width: totalPipes > 0 ? `${Math.max(1, count/totalPipes*100)}%` : "1%",
                      opacity: 0.85, transition: "width 0.6s ease",
                    }} />
                  </div>
                </div>
              ))}
              <div style={{
                marginTop: 14, padding: "10px 12px",
                background: "#060d1a", borderRadius: 6, fontSize: 11, color: "#475569",
              }}>
                ⚠ CIPP-lined pipes credited in model. No pipe age data (0.1% GIS coverage).
              </div>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ── Overflow Model Tab ────────────────────────────────────────────
function ModelTab({ modelSummary, predictions }) {
  const [selectedThresh, setSelectedThresh] = useState(0.40);

  const ms = modelSummary.data;
  const preds = predictions.data || [];

  // Build chart data: all predictions shown as a line
  // eventMarker = prob value on event days (so they appear at correct Y position)
  const scatterData = preds.slice(-730).map(d => ({
    date: d.date,                          // full "YYYY-MM-DD" for tooltip
    prob: d.overflow_prob ?? 0,
    rain: d.rain_in ?? 0,
    overflow_occurred: d.overflow_occurred ?? 0,
    // eventMarker drives the Cell coloring overlay
    eventMarker: d.overflow_occurred ? (d.overflow_prob ?? 0) : null,
  }));

  const thresholds = ms?.thresholds || [];
  const selT = thresholds.find(t => Math.abs(t.t - selectedThresh) < 0.005) || thresholds[2];

  // Feature importance bar data
  const fiData = ms ? Object.entries(ms.feature_importances || {}).map(([k, v]) => ({
    feature: k, importance: v,
  })).sort((a, b) => b.importance - a.importance) : [];

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <StatCard label="ROC-AUC" value={ms?.auc != null ? ms.auc.toFixed(3) : "—"}
          sub={ms?.auc >= 0.90 ? "✦ exceeds spec (0.75)" : "spec threshold: 0.75"}
          accent={ms?.auc >= 0.90 ? "#4ade80" : ms?.auc >= 0.75 ? "#a3e635" : "#f97316"}
          loading={modelSummary.loading} />
        <StatCard label="Avg Precision" value={ms?.avg_precision ?? "—"}
          sub="area under PR curve" accent="#38bdf8" loading={modelSummary.loading} />
        <StatCard label="Brier Score" value={ms?.brier ?? "—"}
          sub="lower = better calibration" accent="#a78bfa" loading={modelSummary.loading} />
        <StatCard label="Event Days" value={ms?.n_events ?? "—"}
          sub={ms ? `of ${ms.n_days?.toLocaleString()} days · ${ms.base_rate_pct}% base rate` : "loading…"}
          accent="#f97316" loading={modelSummary.loading} />
      </div>

      {modelSummary.error && <ErrorPanel msg={modelSummary.error} onRetry={modelSummary.refetch} />}
      {predictions.error  && <ErrorPanel msg={predictions.error}  onRetry={predictions.refetch} />}

      {/* Probability scatter */}
      <Panel style={{ marginBottom: 20 }}>
        <SectionHeader
          title="Overflow Probability — Last 365 Days"
          sub="Orange = actual overflow event · Grey = no overflow · Dashed line = active threshold"
          badge="LIVE"
        />
        {predictions.loading ? <LoadingPanel label="predictions" /> : (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={scatterData} margin={{ top: 5, right: 24, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#64748b", fontSize: 9 }}
                  interval={Math.floor(scatterData.length / 8)}
                  tickFormatter={v => v?.slice(0, 5) || ""}
                />
                <YAxis
                  domain={[0, 1]}
                  tick={{ fill: "#64748b", fontSize: 10 }}
                  tickFormatter={v => `${(v * 100).toFixed(0)}%`}
                  width={38}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine
                  y={selectedThresh}
                  stroke="#f97316"
                  strokeDasharray="4 2"
                  label={{ value: `alert ${(selectedThresh*100).toFixed(0)}%`, fill: "#f97316", fontSize: 9, position: "insideTopRight" }}
                />
                {/* Probability line — all days */}
                <Line
                  type="monotone"
                  dataKey="prob"
                  name="Overflow prob"
                  stroke="#38bdf8"
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={{ r: 4, fill: "#38bdf8" }}
                />
                {/* Event markers — rendered as a Bar with zero width trick using scatter overlay */}
                <Bar
                  dataKey="eventMarker"
                  name="Overflow event"
                  fill="transparent"
                  isAnimationActive={false}
                >
                  {scatterData.map((entry, i) =>
                    entry.overflow_occurred === 1 ? (
                      <Cell key={i} fill="#f97316" />
                    ) : (
                      <Cell key={i} fill="transparent" />
                    )
                  )}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
            {/* Event marker legend row */}
            <div style={{ display: "flex", gap: 20, marginTop: 10, fontSize: 11, color: "#64748b", alignItems: "center" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ display: "inline-block", width: 20, height: 2, background: "#38bdf8" }} />
                Overflow probability
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ color: "#f97316", fontSize: 14, lineHeight: 1 }}>●</span>
                Recorded overflow
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ display: "inline-block", width: 16, height: 2, background: "#f97316", borderTop: "1px dashed #f97316" }} />
                Alert threshold
              </span>
              <span style={{ marginLeft: "auto", color: "#334155", fontFamily: "monospace" }}>
                {preds.length > 0 && `through ${preds[preds.length - 1]?.date}`}
              </span>
            </div>
          </>
        )}
      </Panel>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Threshold calibration */}
        <Panel>
          <SectionHeader title="Threshold Calibration"
            sub="Tradeoff between false alarms and missed events" />
          {modelSummary.loading ? <LoadingPanel label="thresholds" /> : (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
                {thresholds.map(t => (
                  <button key={t.t} onClick={() => setSelectedThresh(t.t)} style={{
                    background: Math.abs(t.t - selectedThresh) < 0.005 ? "#1e3a5f" : "#0c1829",
                    border: `1px solid ${Math.abs(t.t - selectedThresh) < 0.005 ? "#38bdf8" : "#1e293b"}`,
                    borderRadius: 6, padding: "7px 14px", cursor: "pointer",
                    color: "#f8fafc", fontSize: 12, transition: "all 0.15s",
                  }}>
                    {t.t.toFixed(2)}{t.recommended ? " ★" : ""}
                  </button>
                ))}
              </div>
              {selT && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <StatCard label="Precision"
                    value={selT.prec != null ? `${(selT.prec*100).toFixed(0)}%` : "—"}
                    sub="alerts that are real events"
                    accent={selT.prec >= 0.15 ? "#4ade80" : "#f97316"} />
                  <StatCard label="Recall"
                    value={selT.rec != null ? `${(selT.rec*100).toFixed(0)}%` : "—"}
                    sub="events that trigger an alert"
                    accent="#38bdf8" />
                  <StatCard label="F1 Score" value={selT.f1?.toFixed(2) ?? "—"}
                    sub="harmonic mean" accent="#a78bfa" />
                  <StatCard label="Alerts / Year" value={selT.alerts_yr ?? "—"}
                    sub="estimated operator workload"
                    accent={selT.alerts_yr <= 50 ? "#4ade80" : "#f97316"} />
                </div>
              )}
              {ms?.recommended_threshold != null && (
                <div style={{
                  marginTop: 14, padding: "10px 14px",
                  background: "#052e16", border: "1px solid #166534",
                  borderRadius: 6, fontSize: 11, color: "#86efac",
                }}>
                  ★ Recommended threshold: <strong>{ms.recommended_threshold}</strong>
                  {ms.at_040_prec != null && ` — precision ${(ms.at_040_prec*100).toFixed(0)}%, recall ${(ms.at_040_rec*100).toFixed(0)}%, ~${ms.at_040_alerts_yr} alerts/yr`}
                </div>
              )}
            </>
          )}
        </Panel>

        {/* Feature importance */}
        <Panel>
          <SectionHeader title="Feature Importances" sub="Random Forest — higher = more predictive" />
          {modelSummary.loading ? <LoadingPanel label="feature importances" /> : (
            fiData.length === 0 ? (
              <div style={{ color: "#475569", fontSize: 12 }}>No feature data.</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={190}>
                  <BarChart data={fiData} layout="vertical" barSize={14}
                    margin={{ top: 0, right: 20, bottom: 0, left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }}
                      tickFormatter={v => v.toFixed(2)} domain={[0, "dataMax + 0.05"]} />
                    <YAxis type="category" dataKey="feature"
                      tick={{ fill: "#94a3b8", fontSize: 11 }} width={80} />
                    <Tooltip formatter={(v) => v.toFixed(3)} />
                    <Bar dataKey="importance" name="Importance" radius={[0, 3, 3, 0]}>
                      {fiData.map((entry, i) => (
                        <Cell key={i} fill={
                          i === 0 ? "#38bdf8" : i === 1 ? "#2563eb" : i === 2 ? "#7c3aed" : "#334155"
                        } />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{
                  marginTop: 10, padding: "8px 12px",
                  background: "#060d1a", borderRadius: 6, fontSize: 10, color: "#475569",
                }}>
                  Model: {ms?.model || "RandomForest v7"} · LR benchmark AUC: {ms?.lr_benchmark_auc ?? "—"} · AUC is in-sample
                </div>
              </>
            )
          )}
        </Panel>
      </div>
    </div>
  );
}

// ── Pipe Risk Tab ─────────────────────────────────────────────────
function PipeRiskTab({ mapStats }) {
  const [tierFilter, setTierFilter] = useState("Critical");
  const pipeData = useApiData(`/api/pipe-risk?risk_tier=${tierFilter}&limit=200`);

  const features = pipeData.data?.features || [];
  const tierCounts = mapStats.data?.by_tier || {};
  const totalPipes = mapStats.data?.total_pipes || 0;

  // Score composition constants (mirrors serve.py logic for display)
  const scoreComponents = [
    { name: "Material",  max: 40, desc: "VCP=40, CIP=35, RCP=20, PVC=5, CIPP=2" },
    { name: "Diameter",  max: 15, desc: "8\" = 15 (blockage-prone), 12\" = 8, 18\" = 5" },
    { name: "Slope",     max: 15, desc: "<0.3% = 15 (sediment), >3% = 7 (erosion)" },
    { name: "Area",      max: 30, desc: "WBAY=30, SMNT=20 (operational area SSO density)" },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        {["Critical","High","Medium","Low"].map(t => (
          <StatCard key={t} label={t} value={mapStats.loading ? null : (tierCounts[t] ?? "—")}
            sub={totalPipes > 0 ? `${(((tierCounts[t]||0)/totalPipes)*100).toFixed(0)}% of network` : ""}
            accent={tierColor(t)} loading={mapStats.loading} />
        ))}
      </div>

      {mapStats.error && <ErrorPanel msg={mapStats.error} onRetry={mapStats.refetch} />}

      {/* Score composition */}
      <Panel style={{ marginBottom: 20 }}>
        <SectionHeader title="Score Composition" sub="Rule-based model — 4 components, max 100 pts" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {scoreComponents.map(c => (
            <div key={c.name} style={{
              background: "#060d1a", borderRadius: 6, padding: 14,
              borderLeft: `3px solid #1e3a5f`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                {c.name} <span style={{ color: "#475569", fontWeight: 400 }}>/ {c.max} pts</span>
              </div>
              <div style={{ fontSize: 10, color: "#475569", lineHeight: 1.6 }}>{c.desc}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Tier filter + pipe table */}
      <Panel>
        <SectionHeader title="Pipe Segments by Risk Tier"
          sub="Fetched live from gravity_mains.geojson via serve.py" badge="LIVE" />

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {["Critical","High","Medium","Low"].map(t => (
            <button key={t} onClick={() => setTierFilter(t)} style={{
              background: tierFilter === t ? `${tierColor(t)}22` : "#060d1a",
              border: `1px solid ${tierFilter === t ? tierColor(t) : "#1e293b"}`,
              color: tierFilter === t ? tierColor(t) : "#64748b",
              borderRadius: 6, padding: "6px 14px", cursor: "pointer",
              fontSize: 11, fontWeight: tierFilter === t ? 600 : 400,
              transition: "all 0.15s",
            }}>{t}</button>
          ))}
          <div style={{
            marginLeft: "auto", fontSize: 11, color: "#475569",
            alignSelf: "center",
          }}>
            Showing up to 200 of {(tierCounts[tierFilter] || 0).toLocaleString()} {tierFilter} pipes
          </div>
        </div>

        {pipeData.loading ? <LoadingPanel label="pipe segments" /> :
         pipeData.error   ? <ErrorPanel msg={pipeData.error} onRetry={pipeData.refetch} /> : (
          <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead style={{ position: "sticky", top: 0, background: "#0f172a", zIndex: 1 }}>
                <tr style={{ borderBottom: "1px solid #1e293b" }}>
                  {["Facility ID","Material","Size","Area","Score","Tier","Location"].map(h => (
                    <th key={h} style={{
                      textAlign: "left", padding: "7px 10px",
                      color: "#475569", fontWeight: 500,
                      letterSpacing: "0.05em", fontSize: 10,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feat, i) => {
                  const p = feat.properties;
                  return (
                    <tr key={p.id || i} style={{
                      borderBottom: "1px solid #0a1020",
                      background: i % 2 === 0 ? "transparent" : "#060d1a",
                    }}>
                      <td style={{ padding: "6px 10px", fontFamily: "monospace", color: "#64748b", fontSize: 10 }}>{p.id || "—"}</td>
                      <td style={{ padding: "6px 10px", color: p.material === "VCP" ? "#fbbf24" : p.material === "CIP" ? "#f87171" : "#94a3b8" }}>
                        {p.material}
                      </td>
                      <td style={{ padding: "6px 10px", color: "#64748b" }}>{p.size ? `${p.size}"` : "—"}</td>
                      <td style={{ padding: "6px 10px", color: "#475569" }}>{p.area || "—"}</td>
                      <td style={{ padding: "6px 10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 28, height: 5, borderRadius: 2, background: "#1e293b" }}>
                            <div style={{ width: `${p.risk_score}%`, height: "100%", background: p.color, borderRadius: 2 }} />
                          </div>
                          <span style={{ fontFamily: "monospace", color: p.color, fontSize: 11 }}>{p.risk_score}</span>
                        </div>
                      </td>
                      <td style={{ padding: "6px 10px" }}>
                        <span style={{
                          background: `${p.color}22`, color: p.color,
                          borderRadius: 3, padding: "1px 7px",
                          fontSize: 10, fontWeight: 600,
                        }}>{p.risk_tier}</span>
                      </td>
                      <td style={{ padding: "6px 10px", color: "#475569", fontSize: 10, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.location || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{
          marginTop: 12, padding: "8px 12px",
          background: "#060d1a", borderRadius: 6, fontSize: 10, color: "#475569",
        }}>
          ⚠ Limitations: No pipe age (0.1% GIS coverage) · Spatial score uses operational area proxy ·
          Rule-based model — not ML-trained · CIPP-lined pipes capped at material score ≤ 5
        </div>
      </Panel>
    </div>
  );
}

// ── Hot Spots Tab ─────────────────────────────────────────────────
function ManholeCard({ mh, isConstruction, info }) {
  return (
    <div style={{
      background: "#0f172a",
      border: `1px solid ${isConstruction ? "#1e3a5f" : "#7c2d12"}`,
      borderRadius: 8, padding: 18,
      display: "flex", alignItems: "flex-start", gap: 24,
    }}>
      <div style={{ minWidth: 140 }}>
        <div style={{
          fontFamily: "monospace", fontSize: 13,
          color: isConstruction ? "#7dd3fc" : "#38bdf8", fontWeight: 700,
        }}>{mh.manhole_id}</div>
        <div style={{ fontSize: 10, color: "#475569", marginTop: 3 }}>
          {mh.address || (isConstruction ? info?.project : "Repeat I/I")}
        </div>
        {isConstruction && info && (
          <div style={{
            marginTop: 6, fontSize: 10,
            background: "#0c1e38", borderRadius: 4, padding: "3px 8px",
            color: "#38bdf8", display: "inline-block",
          }}>Resolves {info.resolution}</div>
        )}
      </div>
      <div style={{ flex: 1, display: "flex", gap: 28, alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 9, color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase" }}>Events</div>
          <div style={{
            fontSize: 22, fontFamily: "monospace", fontWeight: 700,
            color: isConstruction ? "#64748b" : (mh.events >= 8 ? "#dc2626" : "#f97316"),
          }}>{mh.events}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase" }}>Total Volume</div>
          <div style={{ fontSize: 22, fontFamily: "monospace", fontWeight: 700,
            color: isConstruction ? "#64748b" : "#f8fafc" }}>
            {mh.volume_mg.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 400, color: "#64748b" }}>MG</span>
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", maxWidth: 300 }}>
          {isConstruction
            ? "Permitted release during active construction window — expected at this volume. Exclude from pipe failure analysis."
            : "Consistent wet-weather signature — candidate for CCTV inspection and I/I repair prioritization."}
        </div>
      </div>
    </div>
  );
}

function HotspotsTab({ ssoEvents }) {
  const repeats          = ssoEvents.data?.repeat_manholes || [];
  const constructionInfo = ssoEvents.data?.construction_manholes || {};
  const constructionIds  = new Set(Object.keys(constructionInfo));

  const constructionRepeats = repeats.filter(mh =>  constructionIds.has(mh.manhole_id));
  const genuineRepeats      = repeats.filter(mh => !constructionIds.has(mh.manhole_id));

  const renderSection = (title, sub, color, items, isConstruction) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${color}33` }}>
        <span style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {title}
        </span>
        <span style={{ fontSize: 11, color: "#475569", marginLeft: 10 }}>{sub}</span>
      </div>
      {items.length === 0 ? (
        <div style={{ padding: "14px 16px", background: "#060d1a", borderRadius: 6,
          fontSize: 12, color: "#334155" }}>None detected in 2025 data.</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {items.map(mh => (
            <ManholeCard key={mh.manhole_id} mh={mh} isConstruction={isConstruction}
              info={constructionInfo[mh.manhole_id]} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <SectionHeader
        title="Repeat-Event Manholes — 2025"
        sub="4+ SSO events · split by cause type"
        badge="LIVE"
      />

      {ssoEvents.loading ? <LoadingPanel label="SSO events" /> :
       ssoEvents.error   ? <ErrorPanel msg={ssoEvents.error} onRetry={ssoEvents.refetch} /> :
       repeats.length === 0 ? (
         <div style={{
           padding: 32, background: "#0f172a", border: "1px solid #1e293b",
           borderRadius: 8, color: "#475569", fontSize: 12, textAlign: "center",
         }}>No manholes with 4+ events found in 2025 SSO data.</div>
       ) : (
        <>
          {renderSection(
            "⚠ Construction-Related",
            "Permitted releases · not pipe failures · expected to resolve",
            "#3b82f6",
            constructionRepeats,
            true
          )}
          {renderSection(
            "🔴 Genuine I/I Hotspots",
            "Wet-weather signature · chronic defects · inspect & repair",
            "#ef4444",
            genuineRepeats,
            false
          )}
        </>
      )}

      {/* Interpretation panel */}
      <Panel>
        <SectionHeader title="How to Read This Tab" />
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20,
          fontSize: 12, color: "#94a3b8", lineHeight: 1.7,
        }}>
          <div>
            <div style={{ color: "#93c5fd", fontWeight: 600, marginBottom: 8 }}>Construction-Related</div>
            Permitted overflow points limited by active construction, not pipe failures.
            High event counts and large volumes are expected during project windows.
            Exclude from pipe failure risk model; include in overflow volume forecasting.
            These should resolve when associated projects complete.
          </div>
          <div>
            <div style={{ color: "#fca5a5", fontWeight: 600, marginBottom: 8 }}>Genuine I/I Hotspots</div>
            Manholes appearing in multiple wet seasons with wet-weather cause and consistent
            low volumes (sub-1MG) are genuine infiltration/inflow locations. These warrant
            field inspection regardless of rainfall intensity — low thresholds suggest
            chronic defects in the upstream collection system.
          </div>
        </div>
      </Panel>
    </div>
  );
}

// ── Forecast Tab ─────────────────────────────────────────────────
function ForecastTab() {
  const forecast = useApiData("/api/forecast");

  const days    = forecast.data?.forecast || [];
  const meta    = forecast.data?.meta || {};
  const needsSetup = !forecast.loading && !forecast.data?.forecast &&
                     (forecast.error || forecast.data?.error);

  return (
    <div>
      <SectionHeader
        title="7-Day Overflow Risk Forecast"
        sub="NWS QPF (MRX office · Chattanooga grid) + Random Forest model"
        badge={days.length > 0 ? "LIVE" : undefined}
      />

      {forecast.loading && <LoadingPanel label="NWS forecast + model scoring" />}

      {/* Setup instructions — shown only when model not yet serialized */}
      {!forecast.loading && needsSetup && (
        <Panel style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            <div style={{ fontSize: 40, lineHeight: 1 }}>🌧</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#38bdf8", fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                One setup step required
              </div>
              <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.8, marginBottom: 16 }}>
                The RF model needs to be serialized to disk so the forecast
                endpoint can load it without retraining. Run these two commands
                from your project root:
              </div>
              <div style={{
                background: "#060d1a", border: "1px solid #1e3a5f",
                borderRadius: 6, padding: "14px 18px",
                fontFamily: "monospace", fontSize: 12, lineHeight: 2.2,
                color: "#a5f3fc",
              }}>
                <div><span style={{ color: "#475569" }}># Step 1 — patch model file (one-time)</span></div>
                <div>python src/patch_model_v7.py</div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ color: "#475569" }}># Step 2 — retrain + serialize</span>
                </div>
                <div>python src/model_overflow_v7.py</div>
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 12 }}>
                This writes <code style={{ background: "#0c1829", padding: "1px 5px", borderRadius: 3 }}>data/processed/rf_model.joblib</code>.
                After that, this tab will load automatically on every page refresh.
              </div>
              {forecast.data?.error && (
                <div style={{
                  marginTop: 12, padding: "8px 12px",
                  background: "#1c0a0a", border: "1px solid #7f1d1d",
                  borderRadius: 4, fontSize: 11, color: "#fca5a5",
                }}>
                  API response: {forecast.data.error}
                </div>
              )}
            </div>
          </div>
        </Panel>
      )}

      {/* Live forecast cards */}
      {days.length > 0 && (
        <>
          {/* 7-day risk cards */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 10, marginBottom: 20,
          }}>
            {days.map((day, i) => {
              const prob  = day.overflow_prob;
              const rain  = day.rain_forecast;
              const color = probColor(prob);
              const isAlert = day.alert;

              return (
                <div key={i} style={{
                  background: isAlert ? `${color}12` : "#060d1a",
                  border: `1px solid ${color}${isAlert ? "66" : "22"}`,
                  borderRadius: 10, padding: "16px 10px",
                  textAlign: "center", position: "relative",
                  transition: "all 0.2s",
                }}>
                  {isAlert && (
                    <div style={{
                      position: "absolute", top: -1, left: 0, right: 0,
                      height: 2, background: color, borderRadius: "10px 10px 0 0",
                    }} />
                  )}
                  {/* Day label */}
                  <div style={{
                    fontSize: 11, fontWeight: 600, color: "#64748b",
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    marginBottom: 4,
                  }}>{day.day_label}</div>
                  {/* Date */}
                  <div style={{ fontSize: 9, color: "#334155", marginBottom: 12 }}>
                    {day.date?.slice(5)}
                  </div>
                  {/* Risk % */}
                  <div style={{
                    fontSize: 26, fontWeight: 700,
                    fontFamily: "'IBM Plex Mono', monospace",
                    color, lineHeight: 1,
                  }}>
                    {(prob * 100).toFixed(0)}
                    <span style={{ fontSize: 14, fontWeight: 400 }}>%</span>
                  </div>
                  {/* Risk tier badge */}
                  <div style={{
                    margin: "8px auto 10px",
                    fontSize: 9, fontWeight: 700,
                    color, textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}>{day.risk_tier}</div>
                  {/* Rain bar */}
                  <div style={{
                    background: "#0c1829", borderRadius: 4,
                    height: 4, margin: "0 4px 6px",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%", borderRadius: 4,
                      background: rainColor(rain),
                      width: `${Math.min(100, rain * 50)}%`,
                      transition: "width 0.4s ease",
                    }} />
                  </div>
                  {/* Rainfall */}
                  <div style={{ fontSize: 10, color: rainColor(rain) }}>
                    {rain.toFixed(2)}"
                  </div>
                </div>
              );
            })}
          </div>

          {/* 7-day rolling context chart */}
          <Panel style={{ marginBottom: 16 }}>
            <SectionHeader
              title="Forecast Detail"
              sub="Overflow probability vs forecast precipitation — 7 day window"
            />
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={days} margin={{ top: 5, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="day_label" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis yAxisId="rain" orientation="left"
                  tick={{ fill: "#64748b", fontSize: 10 }}
                  tickFormatter={v => `${v.toFixed(1)}"`}
                  domain={[0, "dataMax + 0.2"]} />
                <YAxis yAxisId="prob" orientation="right"
                  tick={{ fill: "#64748b", fontSize: 10 }}
                  tickFormatter={v => `${(v*100).toFixed(0)}%`}
                  domain={[0, 1]} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine yAxisId="prob" y={0.40} stroke="#f97316"
                  strokeDasharray="4 2"
                  label={{ value: "alert", fill: "#f97316", fontSize: 9, position: "right" }} />
                <Bar yAxisId="rain" dataKey="rain_forecast" name="Forecast rain (in)"
                  fill="#1e3a5f" radius={[3, 3, 0, 0]} />
                <Line yAxisId="prob" type="monotone" dataKey="overflow_prob"
                  name="Overflow prob" stroke="#f97316"
                  strokeWidth={2} dot={{ fill: "#f97316", r: 4 }} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          {/* Meta footer */}
          <div style={{
            padding: "10px 14px", background: "#060d1a",
            border: "1px solid #0f1929", borderRadius: 6,
            display: "flex", justifyContent: "space-between",
            fontSize: 10, color: "#334155",
          }}>
            <span>NWS office: {meta.nws_office} · Grid: {meta.grid}</span>
            <span>Model: {meta.model} · Alert threshold: {meta.threshold}</span>
            <span>Generated: {meta.generated_at?.replace("T", " ").replace("Z", " UTC")}</span>
          </div>
          {meta.note && (
            <div style={{
              marginTop: 8, padding: "8px 14px",
              background: "#1c1000", border: "1px solid #78350f",
              borderRadius: 6, fontSize: 11, color: "#fcd34d",
            }}>
              ⚠ {meta.note}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════

export default function FlowGuardDashboard() {
  const [activeTab,   setActiveTab]   = useState("overview");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["overview"]));

  const handleTabChange = (id) => {
    setActiveTab(id);
    setVisitedTabs(prev => new Set([...prev, id]));
  };

  // ── Shared data fetches — model data is lazy (only when model tab visited) ──
  const modelActive  = visitedTabs.has("model1");
  const modelSummary = useApiData(modelActive ? "/api/model-summary" : null);
  const predictions  = useApiData(modelActive ? "/api/predictions"   : null);
  const ssoEvents    = useApiData("/api/sso-events");
  const mapStats     = useApiData("/api/map/stats");
  const serverStatus = useApiData("/api/status");

  const tabs = [
    { id: "overview",  label: "Overview" },
    { id: "model1",    label: "Overflow Model" },
    { id: "pipes",     label: "Pipe Risk" },
    { id: "hotspots",  label: "Hot Spots" },
    { id: "forecast",  label: "Forecast ✦" },
    { id: "map",       label: "Network Map" },
  ];

  const anyError = modelSummary.error || predictions.error || ssoEvents.error || mapStats.error;
  const allLoaded = !modelSummary.loading && !predictions.loading && !ssoEvents.loading && !mapStats.loading;
  const serverOk = serverStatus.data?.status === "ok";
  const mostRecentDate = predictions.data?.at(-1)?.date;

  return (
    <>
      {/* Keyframe for spinner */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #0c1829; }
        ::-webkit-scrollbar-thumb { background: #1e3a5f; border-radius: 3px; }
      `}</style>

      <div style={{
        background: "#020817", minHeight: "100vh", color: "#f8fafc",
        fontFamily: "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif",
        fontSize: 13,
      }}>

        {/* ── Header ── */}
        <div style={{
          borderBottom: "1px solid #1e293b",
          padding: "12px 28px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "linear-gradient(180deg, #0c1829 0%, #020817 100%)",
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Logo mark */}
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, boxShadow: "0 0 12px #2563eb44",
            }}>⊕</div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", color: "#f8fafc" }}>FlowGuard</span>
                <span style={{
                  fontSize: 10, color: "#38bdf8", fontWeight: 600,
                  background: "#0c2a4a", border: "1px solid #1e3a5f",
                  padding: "1px 8px", borderRadius: 10,
                  letterSpacing: "0.04em",
                }}>by ClearFlow Analytics</span>
              </div>
              <div style={{ fontSize: 10, color: "#334155", letterSpacing: "0.06em", marginTop: 2 }}>
                CHATTANOOGA MSD · SSO PREDICTION SYSTEM · PILOT v0.1
              </div>
            </div>
          </div>

          {/* Status indicators */}
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {serverStatus.loading ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#475569" }}>
                <Spinner size={12} /> Connecting…
              </div>
            ) : serverOk ? (
              <div style={{
                background: "#052e16", border: "1px solid #166534",
                borderRadius: 20, padding: "3px 12px",
                fontSize: 11, color: "#4ade80",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span style={{ animation: "pulse 2s infinite" }}>●</span> FLASK CONNECTED
              </div>
            ) : (
              <div style={{
                background: "#1c0a0a", border: "1px solid #7f1d1d",
                borderRadius: 20, padding: "3px 12px",
                fontSize: 11, color: "#fca5a5",
              }}>
                ● FLASK OFFLINE — run python src/serve.py
              </div>
            )}
            {anyError && (
              <div style={{
                background: "#1c0a0a", border: "1px solid #7f1d1d",
                borderRadius: 20, padding: "3px 12px",
                fontSize: 11, color: "#fca5a5",
              }}>⚠ Data errors</div>
            )}
            <div style={{ color: "#334155", fontSize: 10 }}>
              {mostRecentDate ? `Data through ${mostRecentDate}` : allLoaded ? "No predictions loaded" : "Loading…"}
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div style={{
          borderBottom: "1px solid #1e293b",
          padding: "0 28px",
          display: "flex", gap: 0,
          background: "#020817",
        }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => handleTabChange(tab.id)} style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "10px 18px", fontSize: 12,
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? "#38bdf8" : "#475569",
              borderBottom: `2px solid ${activeTab === tab.id ? "#38bdf8" : "transparent"}`,
              transition: "all 0.15s", letterSpacing: "0.01em",
            }}>{tab.label}</button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div style={{ padding: "24px 28px", animation: "fadeIn 0.25s ease-out" }} key={activeTab}>
          {activeTab === "overview"  && <ErrorBoundary><OverviewTab mapStats={mapStats} ssoEvents={ssoEvents} /></ErrorBoundary>}
          {activeTab === "model1"    && <ErrorBoundary><ModelTab modelSummary={modelSummary} predictions={predictions} /></ErrorBoundary>}
          {activeTab === "pipes"     && <ErrorBoundary><PipeRiskTab mapStats={mapStats} /></ErrorBoundary>}
          {activeTab === "hotspots"  && <ErrorBoundary><HotspotsTab ssoEvents={ssoEvents} /></ErrorBoundary>}
          {activeTab === "forecast"  && <ErrorBoundary><ForecastTab /></ErrorBoundary>}
          {activeTab === "map"       && (
            <div>
              <div style={{
                background: "#0f172a", border: "1px solid #1e293b",
                borderRadius: 8, padding: 20,
              }}>
                <div style={{ marginBottom: 16, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                      Interactive Pipe Network
                    </div>
                    <div style={{ fontSize: 11, color: "#475569", marginTop: 3 }}>
                      Live GIS data from pwgis.chattanooga.gov · color-coded by risk tier
                    </div>
                  </div>
                  <span style={{
                    background: "#0f2d1a", border: "1px solid #166534",
                    color: "#4ade80", fontSize: 10, padding: "2px 8px", borderRadius: 10,
                  }}>LIVE</span>
                </div>
                <PipeNetworkMap />
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          borderTop: "1px solid #0f1929", padding: "10px 28px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: 10, color: "#1e3a5f",
        }}>
          <span>FlowGuard v0.1 · <span style={{ color: "#334155" }}>by ClearFlow Analytics</span></span>
          <span>Random Forest (overflow) + Rule-based (pipe risk) · scikit-learn 1.x</span>
          <span style={{ color: "#1e293b" }}>Not for regulatory reporting</span>
        </div>
      </div>
    </>
  );
}
